import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBookingContext } from '@common/context/BookingContext';
import {
  Timetable_timetable_groups_rows as RowType,
  Timetable_timetable_groups_rows_columns as Column,
} from '@typings/api';
import { DateStyle, formatTime, formatToLocaleDateString } from '@utils/date';

import Slot from './Slot';
interface Props {
  onSlotSelect: (slot: Column) => void;
  row: RowType;
  timezone: string;
}

function Row(props: Props) {
  const { onSlotSelect, row, timezone } = props;
  const [selectedSlotIndex, setSelectedSlotIndex] = useState(-1);
  const rowRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const [{ customerValid }] = useBookingContext();

  const timeSlot = `${formatTime(row.startDate, timezone)} - ${formatTime(
    row.endDate,
    timezone,
  )}`;

  function handleSlotClick(index: number) {
    if (index === selectedSlotIndex) {
      return setSelectedSlotIndex(-1);
    }

    return setSelectedSlotIndex(index);
  }

  function handleSlotSelect(index: number) {
    return onSlotSelect(row.columns[index]);
  }

  const handleClickOutsideRow = useCallback(
    (event: MouseEvent) => {
      let shouldClose = false;
      const rowNode = rowRef.current;
      if (rowNode && !rowNode.contains(event.target as Node)) {
        shouldClose = true;
      }
      let node = event.target as Node;
      while (node != null) {
        if ((node as HTMLElement).classList?.contains('ReactModal__Content')) {
          shouldClose = false;
        }
        node = node.parentNode as Node;
      }
      if (shouldClose) {
        setSelectedSlotIndex(-1);
      }
    },
    [rowRef],
  );

  useEffect(() => {
    if (selectedSlotIndex > -1) {
      document.addEventListener('click', handleClickOutsideRow);
    }

    return () => {
      document.removeEventListener('click', handleClickOutsideRow);
    };
  }, [handleClickOutsideRow, selectedSlotIndex]);

  return (
    <div className="flex space-x-1 xl:space-x-2" ref={rowRef}>
      {row.columns.map((column, i) => {
        const date =
          column.startDate &&
          formatToLocaleDateString(column.startDate, i18n.language, {
            day: DateStyle.NUMERIC,
            month: DateStyle.LONG,
            timezone,
          });

        return (
          <Slot
            column={column}
            date={date}
            isDisabled={!customerValid}
            isExpanded={selectedSlotIndex === i}
            key={column.key}
            onClick={handleSlotClick}
            onSelect={handleSlotSelect}
            order={i}
            timeSlot={timeSlot}
          />
        );
      })}
    </div>
  );
}

export default memo(Row);
